<template>
  <section v-if="products.length > 0" class="featured-hardware-section my-12 relative z-10">
    <div class="container mx-auto px-4">
      
      <!-- Section Header with Attractive Slogans -->
      <div class="flex flex-col md:flex-row md:items-end justify-between mb-8 pb-4 border-b border-slate-200 dark:border-slate-800 gap-4">
        <div>
          <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400 text-xs font-black uppercase tracking-wider mb-2">
            <span class="animate-pulse">⚡</span>
            <span>Handpicked Tech Deals</span>
          </div>

          <h2 class="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight flex items-center gap-2.5">
            <span>Featured Hardware & Gaming Gear</span>
            <span class="text-blue-600 dark:text-blue-400">💎</span>
          </h2>

          <p class="text-sm sm:text-base text-slate-600 dark:text-slate-400 mt-1.5 max-w-2xl">
            Certified authentic laptops, consoles & premium tech with express Cash on Delivery across all 58 Wilayas.
          </p>
        </div>

        <!-- View Full Catalog CTA -->
        <router-link 
          to="/physical-catalog" 
          class="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs sm:text-sm text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800/60 hover:bg-blue-600 hover:text-white dark:hover:bg-blue-600 dark:hover:text-white transition-all duration-300 shadow-sm shrink-0 group"
        >
          <span>Explore All Hardware</span>
          <span class="group-hover:translate-x-1 transition-transform">→</span>
        </router-link>
      </div>

      <!-- Products Grid (Matching Catalogue Style) -->
      <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
        <article 
          v-for="product in products" 
          :key="product.id"
          class="group relative bg-white dark:bg-slate-900/90 border border-slate-200/80 dark:border-slate-800 rounded-2xl p-2.5 sm:p-3.5 shadow-md hover:shadow-xl hover:border-blue-500/50 transition-all duration-300 hover:-translate-y-1 flex flex-col justify-between overflow-hidden"
        >
          <!-- Top Accent Gradient Line -->
          <div class="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>

          <div>
            <!-- Product Image Frame -->
            <router-link 
              :to="`/physical-product/${product.slug}`" 
              :title="`View ${product.brand} ${product.model_name}`"
              class="block relative aspect-square rounded-xl bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-800/80 dark:to-slate-900 border border-slate-200/60 dark:border-slate-700/60 overflow-hidden mb-2.5 cursor-pointer group-hover:border-blue-500/30 transition-colors"
            >
              <img 
                :src="resolveImageUrl(product.main_image)" 
                :alt="`${product.brand} ${product.model_name}`" 
                @error="(e) => e.target.src = '/placeholder-image.png'"
                loading="lazy"
                class="w-full h-full object-contain p-2 transition-transform duration-500 group-hover:scale-105"
              />
              
              <!-- Condition Tag -->
              <div class="absolute top-1.5 left-1.5 px-1.5 py-0.5 text-[8px] sm:text-[9px] font-black rounded bg-slate-900/85 text-blue-400 border border-blue-500/30 backdrop-blur-md shadow-sm">
                {{ formatCondition(product.condition_grade) }}
              </div>

              <!-- Promo Tag -->
              <div 
                v-if="product.sale_price && product.sale_price < product.price" 
                class="absolute top-1.5 right-1.5 px-1.5 py-0.5 text-[8px] sm:text-[9px] font-black rounded bg-red-500 text-white shadow-sm"
              >
                PROMO
              </div>
            </router-link>

            <!-- Category & Brand Row -->
            <div class="flex items-center justify-between gap-1 mb-0.5">
              <span class="text-[10px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-wider truncate">
                {{ product.brand }}
              </span>
              <span class="text-[9px] font-bold text-slate-400 truncate">
                {{ product.category?.name }}
              </span>
            </div>

            <!-- Model Name -->
            <router-link 
              :to="`/physical-product/${product.slug}`"
              class="block text-xs sm:text-sm font-bold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-1 leading-snug"
            >
              {{ product.model_name }}
            </router-link>
          </div>

          <!-- Price & Purchase CTA -->
          <div class="pt-2 border-t border-slate-100 dark:border-slate-800 mt-2.5 flex items-center justify-between gap-1">
            <div>
              <span class="text-xs sm:text-sm font-black text-blue-600 dark:text-blue-400 block leading-tight">
                {{ formatPrice(product.price) }} DZD
              </span>
              <span v-if="product.sale_price && product.sale_price < product.price" class="text-[9px] text-slate-400 line-through block">
                {{ formatPrice(product.sale_price) }} DZD
              </span>
            </div>

            <router-link 
              :to="`/physical-product/${product.slug}`"
              class="px-2.5 py-1 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-black text-[10px] sm:text-xs uppercase tracking-wider transition-all shadow-sm flex items-center gap-0.5 active:scale-95 shrink-0"
            >
              <span>View</span>
              <span>&rarr;</span>
            </router-link>
          </div>
        </article>
      </div>

    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { physicalProductService } from '@/services/api.js';

const products = ref([]);
const loading = ref(false);

const resolveImageUrl = (img) => {
  if (!img || typeof img !== 'string') return '/placeholder-image.png';
  if (img.startsWith('http://') || img.startsWith('https://')) {
    if (typeof window !== 'undefined' && window.location.protocol === 'https:' && img.startsWith('http://api.eskrag.com')) {
      return img.replace('http://', 'https://');
    }
    return img;
  }
  const base = 'https://api.eskrag.com';
  return `${base}/${img.replace(/^\/+/, '')}`;
};

const formatPrice = (val) => {
  if (!val) return '0.00';
  return Number(val).toLocaleString('fr-DZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatCondition = (grade) => {
  const map = {
    'new': 'Brand New',
    'open_box': 'Open Box',
    'refurbished_a': 'Refurbished Grade A',
    'refurbished_b': 'Refurbished Grade B',
    'used_good': 'Used - Good'
  };
  return map[grade] || 'Certified Tech';
};

const loadFeaturedProducts = async () => {
  loading.value = true;
  try {
    const res = await physicalProductService.getFeaturedProducts();
    if (res && res.success && Array.isArray(res.data)) {
      products.value = res.data;
    }
  } catch (err) {
    console.error('Failed to load featured physical products:', err);
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  loadFeaturedProducts();
});
</script>

<style scoped>
.featured-hardware-section {
  animation: fadeIn 0.4s ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
